import React, { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import logoImg from 'assets/img/main_logo.png';

export default function WarrantyCertificateModal({
  isOpen,
  onClose,
  productId,
}) {
  const [apiData, setApiData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const printRef = useRef(null);

  // Helper to format ISO dates to DD/MM/YYYY
  const formatDate = (val) => {
    if (!val) return '';
    const m = moment(val);
    return m.isValid() ? m.format('DD/MM/YYYY') : String(val);
  };

  // Fetch real certificate data from submissions API
  useEffect(() => {
    if (!isOpen || !productId) {
      setApiData(null);
      setErrorMessage('');
      return;
    }

    let isMounted = true;
    const loadCertificate = async () => {
      setLoading(true);
      setErrorMessage('');
      setApiData(null);
      try {
        const apiUrl = `https://submissions.aquasense.tech/api/customer/product/${encodeURIComponent(productId)}`;
        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        const json = await res.json().catch(() => null);

        if (!isMounted) return;

        if (res.ok && json && json.success && json.data) {
          setApiData(json.data);
          setErrorMessage('');
        } else {
          const msg = json?.message || `Product ID '${productId}' not found.`;
          setErrorMessage(msg);
          setApiData(null);
        }
      } catch (err) {
        console.warn('Warranty API fetch error:', err);
        if (isMounted) {
          setErrorMessage(`Product ID '${productId}' not found.`);
          setApiData(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCertificate();

    return () => {
      isMounted = false;
    };
  }, [isOpen, productId, retryCount]);

  // Normalize API data into Key-Value rows: [{ particular, details }]
  // Exclude: latitude, longitude, maps_url, images, additional_notes, status, submittedAt, null, and empty values
  const tableRows = useMemo(() => {
    if (!apiData) return [];

    const rows = [];
    const addRow = (particular, value) => {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        return;
      }
      rows.push({ particular, details: String(value) });
    };

    const unit = apiData.unit || {};

    // 1. Customer / Client details
    if (apiData.company_name) addRow('Client Name', apiData.company_name);
    if (apiData.customer_name) addRow('Contact Name', apiData.customer_name);
    if (apiData.phone) addRow('Contact No.', apiData.phone);
    if (apiData.email) addRow('Email', apiData.email);
    if (apiData.company_address) addRow('Company Address', apiData.company_address);
    if (apiData.location || unit.location) addRow('Location', apiData.location || unit.location);

    // 2. Unit / Device details
    const pid = unit.product_id || apiData.productId || productId;
    if (pid) addRow('Product ID', pid);
    if (unit.product_name) addRow('Device Type', unit.product_name);
    if (unit.category) addRow('Category', unit.category);
    if (unit.qc_date) addRow('Q.C. Date', formatDate(unit.qc_date));
    if (unit.dispatch_date) addRow('Dispatch Date', formatDate(unit.dispatch_date));
    if (unit.model || unit.meter_model) addRow('Flowmeter Model', unit.model || unit.meter_model);
    if (unit.serial_no) addRow('Flowmeter Serial No.', unit.serial_no);
    if (unit.pipe_size) {
      const pSize = String(unit.pipe_size);
      addRow(
        'Pipe Diameter / Size',
        pSize.toLowerCase().includes('mm') || pSize.toLowerCase().includes('inch') ? pSize : `${pSize} mm`
      );
    }
    if (unit.meter_type) addRow('Meter Type', unit.meter_type);
    if (unit.cable_length) addRow('Sensor Cable Length', unit.cable_length);
    if (unit.firmware_version) addRow('Firmware Version', unit.firmware_version);
    if (unit.testing_id) addRow('Testing ID', unit.testing_id);
    if (unit.telemetry_type) addRow('Telemetry Type', unit.telemetry_type);
    if (unit.tank_type) addRow('Tank Type', unit.tank_type);
    if (unit.comm_type) addRow('Communication Type', unit.comm_type);
    if (unit.power_source) addRow('Power Source', unit.power_source);
    if (unit.remarks) addRow('Remarks', unit.remarks);

    // 3. Warranty details
    if (unit.warranty_duration) addRow('Warranty Duration', unit.warranty_duration);
    if (unit.warranty_start_date && unit.warranty_end_date) {
      addRow(
        'Warranty Period',
        `${formatDate(unit.warranty_start_date)} to ${formatDate(unit.warranty_end_date)}`
      );
    } else if (unit.warranty_start_date) {
      addRow('Warranty Start Date', formatDate(unit.warranty_start_date));
    }
    if (unit.warranty_status) addRow('Warranty Status', unit.warranty_status);

    return rows;
  }, [apiData, productId]);

  const handlePrint = () => {
    if (!printRef.current) return;

    // Remove any previous print iframes
    const oldIframes = document.querySelectorAll('.certificate-print-iframe');
    oldIframes.forEach((el) => el.remove());

    // Create an off-screen iframe with real A4 dimensions for accurate Chromium layout
    const iframe = document.createElement('iframe');
    iframe.className = 'certificate-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '794px';
    iframe.style.height = '1123px';
    iframe.style.border = 'none';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    iframe.style.zIndex = '-9999';
    document.body.appendChild(iframe);

    const contentHtml = printRef.current.innerHTML;

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <base href="${window.location.origin}/" />
          <title>Warranty_Certificate_${productId || 'FTU'}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 0 !important;
            }
            * {
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-family: 'Times New Roman', Times, Georgia, serif !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              font-family: 'Times New Roman', Times, Georgia, serif !important;
              color: #000000 !important;
              width: 100% !important;
            }
            .certificate-print-root {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 auto !important;
              padding: 12mm 16mm 10mm 16mm !important;
              box-sizing: border-box !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .warranty-certificate-printable {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
              border: none !important;
              border-radius: 0 !important;
              background: #ffffff !important;
            }
            table {
              border-collapse: collapse !important;
              width: 100% !important;
            }
            img {
              max-height: 48px !important;
              object-fit: contain !important;
            }
          </style>
        </head>
        <body>
          <div class="certificate-print-root">
            ${contentHtml}
          </div>
        </body>
      </html>
    `);
    doc.close();

    const executePrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print trigger error:', e);
      }
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 3000);
    };

    // Ensure all images are loaded before opening the print preview dialog
    const docImages = doc.images;
    if (!docImages || docImages.length === 0) {
      setTimeout(executePrint, 150);
    } else {
      let loadedCount = 0;
      const totalImages = docImages.length;
      let printFired = false;

      const checkAllLoaded = () => {
        if (!printFired && loadedCount >= totalImages) {
          printFired = true;
          setTimeout(executePrint, 150);
        }
      };

      for (let i = 0; i < totalImages; i++) {
        const img = docImages[i];
        if (img.complete && img.naturalWidth > 0) {
          loadedCount++;
        } else {
          img.onload = img.onerror = () => {
            loadedCount++;
            checkAllLoaded();
          };
        }
      }
      checkAllLoaded();

      // Safety fallback timer
      setTimeout(() => {
        if (!printFired) {
          printFired = true;
          executePrint();
        }
      }, 500);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="warranty-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal Container */}
      <div
        style={{
          width: '100%',
          maxWidth: '860px',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.45)',
          overflow: 'hidden',
        }}
      >
        {/* Modal Action Bar (Header) */}
        <div
          className="no-print"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            background: '#0A4C7A',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            zIndex: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: '15px', letterSpacing: '.02em', color: '#ffffff' }}>
              Warranty Certificate · {productId || 'FTU'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={handlePrint}
              disabled={!apiData || loading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: !apiData || loading ? '#64748b' : '#1072bb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '7px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: !apiData || loading ? 'not-allowed' : 'pointer',
                opacity: !apiData || loading ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Print / Save PDF (1 Page)
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                lineHeight: 1,
              }}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable Document Viewport */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            padding: loading || !apiData ? '0' : '24px 28px 28px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: loading || !apiData ? 'center' : 'flex-start',
          }}
        >
          {loading ? (
            /* Loading State */
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 24px',
                textAlign: 'center',
                width: '100%',
              }}
            >
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  border: '3.5px solid #e2e8f0',
                  borderTop: '3.5px solid #0A4C7A',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '16px',
                }}
              />
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#0A4C7A', marginBottom: '4px' }}>
                Fetching Warranty Certificate...
              </div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                Loading verified warranty details for Product ID: <strong>{productId}</strong>
              </div>
            </div>
          ) : !apiData || tableRows.length === 0 ? (
            /* Not Found / Error State */
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 24px',
                textAlign: 'center',
                width: '100%',
                maxWidth: '520px',
                margin: 'auto',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>

              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
                Warranty Certificate Not Available
              </h3>

              <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 20px', lineHeight: '1.5' }}>
                {errorMessage || `Product ID '${productId}' was not found in the warranty records.`}
              </p>

              <div
                style={{
                  width: '100%',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '14px 20px',
                  fontSize: '13px',
                  color: '#475569',
                  marginBottom: '24px',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ marginBottom: '4px' }}>
                  <strong>Product ID:</strong>{' '}
                  <span style={{ fontFamily: 'monospace', color: '#0A4C7A', fontWeight: 700 }}>
                    {productId || '—'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  Please ensure this device has been registered and quality-checked in the warranty submission system.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setRetryCount((c) => c + 1)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 20px',
                    backgroundColor: '#0A4C7A',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Retry
                </button>
                <button
                  onClick={onClose}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            /* Clean Printable A4 Certificate Sheet (Matching Screen and Print 1:1) */
            <div
              ref={printRef}
              className="warranty-certificate-printable"
              style={{
                width: '100%',
                maxWidth: '780px',
                backgroundColor: '#ffffff',
                color: '#000000',
                fontFamily: "'Times New Roman', Times, Georgia, serif",
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              {/* Top Corporate Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '28px',
                  paddingBottom: '14px',
                  borderBottom: '1.5px solid #0A4C7A',
                  fontFamily: "'Times New Roman', Times, Georgia, serif",
                }}
              >
                {/* Logo & Company Name */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <img
                    src={logoImg}
                    alt="Aquasense Logo"
                    style={{ height: '50px', objectFit: 'contain', marginBottom: '4px' }}
                  />
                  <div
                    style={{
                      fontSize: '15.5px',
                      fontWeight: 700,
                      color: '#0A4C7A',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      fontFamily: "'Times New Roman', Times, Georgia, serif",
                    }}
                  >
                    Aquasense Private Limited
                  </div>
                  <div
                    style={{
                      fontSize: '8.5px',
                      fontWeight: 700,
                      color: '#7FA6C7',
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      marginTop: '2px',
                      fontFamily: "'Times New Roman', Times, Georgia, serif",
                    }}
                  >
                    Sensing a Water Secure Tomorrow
                  </div>
                </div>

                {/* Corporate Contact Info */}
                <div
                  style={{
                    textAlign: 'left',
                    fontSize: '9.5px',
                    color: '#000000',
                    lineHeight: '1.5',
                    flex: 1,
                    paddingLeft: '12px',
                    fontFamily: "'Times New Roman', Times, Georgia, serif",
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5px' }}>
                    <span>✉ <strong style={{ color: '#0A4C7A' }}>info@aquasense.tech</strong></span>
                    <span style={{ fontSize: '8.5px', color: '#222222' }}>
                      <strong>CIN:</strong> U74999DL2017PTC310851 &nbsp;|&nbsp; <strong>GSTIN:</strong> 09AAPCA1102M1ZN
                    </span>
                  </div>
                  <div style={{ marginBottom: '2px' }}>
                    📞 +91-9654085080, +91-7428576523
                  </div>
                  <div style={{ marginTop: '2px' }}>
                    📍 <strong>Regd. Office:</strong> Unit 501A, 5th Floor, Salcon Ras Vilas, District Center, Saket, New Delhi, 110017
                  </div>
                  <div>
                    <strong>Corporate Office:</strong> Silver Spring, Plot 13-14, Ground Floor, Sector - 126, Noida - 201303, Uttar Pradesh
                  </div>
                  <div style={{ color: '#0A4C7A', fontWeight: 700, marginTop: '2.5px' }}>🌐 www.aquasense.tech</div>
                </div>
              </div>

              {/* Certificate Title */}
              <div style={{ textAlign: 'center', marginTop: '12px', marginBottom: '10px', fontFamily: "'Times New Roman', Times, Georgia, serif" }}>
                <h1
                  style={{
                    fontSize: '25px',
                    fontWeight: 700,
                    color: '#0B3B60',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    margin: '0 0 4px',
                    fontFamily: "'Times New Roman', Times, Georgia, serif",
                  }}
                >
                  Warranty Certificate
                </h1>
                <div
                  style={{
                    height: '2px',
                    width: '50px',
                    background: '#0B3B60',
                    margin: '4px auto 6px',
                  }}
                ></div>
                <h3
                  style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: '#0B3B60',
                    margin: '3px 0 6px',
                    fontFamily: "'Times New Roman', Times, Georgia, serif",
                  }}
                >
                  {apiData?.unit?.warranty_duration
                    ? `${apiData.unit.warranty_duration} Warranty`
                    : 'One Year Warranty'}{' '}
                  – {apiData?.unit?.product_name || 'Flow Meter'}
                </h3>
                <p
                  style={{
                    fontSize: '11px',
                    color: '#000000',
                    lineHeight: '1.45',
                    maxWidth: '700px',
                    margin: '0 auto',
                    fontFamily: "'Times New Roman', Times, Georgia, serif",
                  }}
                >
                  This is to certify that the {apiData?.unit?.product_name || 'Flow Meter'} device identified below
                  has been covered under a{' '}
                  {apiData?.unit?.warranty_duration ? apiData.unit.warranty_duration.toLowerCase() : 'one-year'}{' '}
                  warranty from the date of Quality Control (Q.C.) / dispatch, subject to the terms and conditions
                  stated below.
                </p>
              </div>

              {/* Particular & Details Key-Value Table */}
              <div style={{ marginBottom: '10px' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '10.5px',
                    border: '1px solid #7FA6C7',
                    fontFamily: "'Times New Roman', Times, Georgia, serif",
                  }}
                >
                  <thead>
                    <tr style={{ background: '#D9E8F5', color: '#000000' }}>
                      <th
                        style={{
                          padding: '4px 10px',
                          textAlign: 'left',
                          fontWeight: 700,
                          border: '1px solid #7FA6C7',
                          width: '33%',
                          fontFamily: "'Times New Roman', Times, Georgia, serif",
                        }}
                      >
                        Particular
                      </th>
                      <th
                        style={{
                          padding: '4px 10px',
                          textAlign: 'left',
                          fontWeight: 700,
                          border: '1px solid #7FA6C7',
                          width: '67%',
                          fontFamily: "'Times New Roman', Times, Georgia, serif",
                        }}
                      >
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <td
                          style={{
                            padding: '3px 10px',
                            fontWeight: 700,
                            color: '#000000',
                            border: '1px solid #7FA6C7',
                            fontFamily: "'Times New Roman', Times, Georgia, serif",
                          }}
                        >
                          {row.particular}
                        </td>
                        <td
                          style={{
                            padding: '3px 10px',
                            fontWeight: 400,
                            color: '#000000',
                            border: '1px solid #7FA6C7',
                            fontFamily: "'Times New Roman', Times, Georgia, serif",
                          }}
                        >
                          {row.details}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Terms and Conditions */}
              <div
                style={{
                  border: '1px solid #7FA6C7',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginBottom: '10px',
                  backgroundColor: '#ffffff',
                  fontFamily: "'Times New Roman', Times, Georgia, serif",
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#0A4C7A',
                    color: '#ffffff',
                    fontSize: '9.5px',
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: '4px',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    marginBottom: '4px',
                    fontFamily: "'Times New Roman', Times, Georgia, serif",
                  }}
                >
                  Warranty Terms &amp; Conditions
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ol
                    style={{
                      margin: 0,
                      paddingLeft: '16px',
                      fontSize: '9.5px',
                      color: '#000000',
                      lineHeight: '1.45',
                      flex: 1,
                      fontFamily: "'Times New Roman', Times, Georgia, serif",
                    }}
                  >
                    <li style={{ marginBottom: '1.5px' }}>
                      {apiData?.unit?.warranty_start_date && apiData?.unit?.warranty_end_date
                        ? `The warranty is valid from ${formatDate(apiData.unit.warranty_start_date)} to ${formatDate(
                          apiData.unit.warranty_end_date
                        )}.`
                        : 'The warranty is valid for one year from the date of dispatch / commissioning.'}
                    </li>
                    <li style={{ marginBottom: '1.5px' }}>
                      The warranty covers manufacturing defects and defects attributable to normal operation of the supplied
                      device.
                    </li>
                    <li style={{ marginBottom: '1.5px' }}>
                      Warranty does not cover damage caused by improper installation, physical damage, unauthorized
                      modification/repair, misuse, electrical surge, accident, natural calamity, or operation outside
                      specified conditions.
                    </li>
                    <li style={{ marginBottom: '1.5px' }}>
                      Warranty service may involve inspection, repair, replacement of defective parts, or replacement of the
                      device at the discretion of the service provider.
                    </li>
                    <li>
                      Warranty claims should be raised with the Product ID and relevant device details for verification.
                    </li>
                  </ol>

                  <div
                    style={{
                      textAlign: 'center',
                      paddingLeft: '16px',
                      borderLeft: '1px solid #e2e8f0',
                      minWidth: '105px',
                      fontFamily: "'Times New Roman', Times, Georgia, serif",
                    }}
                  >
                    <div
                      style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        color: '#7FA6C7',
                        letterSpacing: '0.16em',
                        lineHeight: '1.35',
                        textTransform: 'uppercase',
                        fontFamily: "'Times New Roman', Times, Georgia, serif",
                      }}
                    >
                      Cleaner Data
                      <br />
                      Brighter
                      <br />
                      Tomorrows
                    </div>
                    <div style={{ width: '28px', height: '1.5px', background: '#7FA6C7', margin: '4px auto 0' }}></div>
                  </div>
                </div>
              </div>

              {/* Electronic Generation Notice */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '24px 0 14px',
                  fontSize: '11px',
                  fontStyle: 'italic',
                  color: '#334155',
                  fontFamily: "'Times New Roman', Times, Georgia, serif",
                  letterSpacing: '0.02em',
                }}
              >
                This certificate is electronically generated, signature is not required.
              </div>

              {/* Bottom Footer */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '8px',
                  borderTop: '1px solid #cbd5e1',
                  fontSize: '8.5px',
                  color: '#7FA6C7',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  fontFamily: "'Times New Roman', Times, Georgia, serif",
                }}
              >
                <span>AQUASENSE PRIVATE LIMITED &nbsp;|&nbsp; SENSING A WATER SECURE TOMORROW</span>
                <span style={{ color: '#0A4C7A' }}>www.aquasense.tech</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
